import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { publicApi } from '../utils/api';

const SiteConfigContext = createContext({});

export function SiteConfigProvider({ children }) {
  const [siteConfig, setSiteConfig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get('/api/config/all');
        setSiteConfig(data);

        const si = data?.site_identity;
        const FALLBACK_ICON = '/api/uploads/images/aga-icon-fallback.png';

        // Update favicon dynamically
        const faviconSrc = si?.favicon_url || FALLBACK_ICON;
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = faviconSrc;

        // Update document title
        if (si?.site_name) {
          document.title = si.site_name;
        }

        // Update OG meta tags for social sharing
        const setMeta = (property, content) => {
          if (!content) return;
          let el = document.querySelector(`meta[property='${property}']`);
          if (!el) {
            el = document.createElement('meta');
            el.setAttribute('property', property);
            document.head.appendChild(el);
          }
          el.setAttribute('content', content);
        };

        if (si?.site_name) setMeta('og:site_name', si.site_name);
        if (si?.site_name) setMeta('og:title', si.site_name + (si.tagline ? ' - ' + si.tagline : ''));
        if (si?.description) setMeta('og:description', si.description);
        if (si?.og_image_url) {
          const ogImg = si.og_image_url.startsWith('/api') ? `${window.location.origin}${si.og_image_url}` : si.og_image_url;
          setMeta('og:image', ogImg);
          setMeta('twitter:image', ogImg);
          setMeta('twitter:card', 'summary_large_image');
        }
      } catch (e) {
      console.error('Failed to load site config:', e);
      // Fallback to defaults
    }
    })();
  }, []);

  const logoUrl = siteConfig?.site_identity?.logo_url || '';
  const iconUrl = siteConfig?.site_identity?.icon_url || '';
  const siteName = siteConfig?.site_identity?.site_name || 'All Global Access';
  const ogImageUrl = siteConfig?.site_identity?.og_image_url || '';

  // Memoize so consumers don't re-render unless the underlying config changed.
  const value = useMemo(
    () => ({ siteConfig, logoUrl, iconUrl, siteName, ogImageUrl }),
    [siteConfig, logoUrl, iconUrl, siteName, ogImageUrl],
  );

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
